import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Github,
} from "lucide-react";
import { usePortfolio } from "../../contexts/PortfolioContext";
import { normalizeProjects } from "../../utils/projectMedia";
import SectionHeading from "../public/SectionHeading";

const PAGE_SIZE = 7;

const Projects = () => {
  const { portfolioData } = usePortfolio();
  const projects = useMemo(
    () => normalizeProjects(portfolioData.projects || []),
    [portfolioData.projects],
  );

  const [filter, setFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const rootRef = useRef(null);
  const navigate = useNavigate();

  const tags = projects.reduce((acc, project) => {
    project.tags.forEach((tag) => {
      if (!acc.includes(tag)) acc.push(tag);
    });
    return acc;
  }, []);

  const filters = ["ALL", ...tags.slice(0, 5)];

  const filteredProjects =
    filter === "ALL"
      ? projects
      : projects.filter((project) => project.tags.includes(filter));

  const totalPages = Math.ceil(filteredProjects.length / PAGE_SIZE);
  const pagedProjects = filteredProjects.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const featured =
    filter === "ALL" && page === 1 && pagedProjects.length >= 4
      ? pagedProjects[0]
      : null;
  const gridProjects = featured ? pagedProjects.slice(1) : pagedProjects;

  const handleFilter = (tag) => {
    setFilter(tag);
    setPage(1);
  };

  const openProject = (project) => {
    if (!project?.slug) return;
    navigate(`/project/${project.slug}`);
  };

  const goPage = (nextPage) => {
    setPage(nextPage);
    if (rootRef.current) {
      rootRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section
      className="public-section public-section--white"
      id="projects"
      ref={rootRef}
    >
      <div className="container">
        <SectionHeading
          eyebrow="Selected work"
          title="Projects"
          description="Backend-heavy builds, full-stack products, and a few experiments shipped along the way."
          aside={
            filteredProjects.length > 0 && (
              <span className="section-count">
                {filteredProjects.length} shipped
              </span>
            )
          }
        />

        <div className="project-filters" aria-label="Filter projects">
          {filters.map((tag) => (
            <button
              key={tag}
              type="button"
              className={`filter-chip${filter === tag ? " is-active" : ""}`}
              aria-pressed={filter === tag}
              onClick={() => handleFilter(tag)}
            >
              {tag === "ALL" ? "All projects" : tag}
            </button>
          ))}
        </div>

        {featured && (
          <article
            className="featured-project"
            role="link"
            tabIndex={0}
            aria-label={`Open ${featured.title}`}
            onClick={() => openProject(featured)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openProject(featured);
              }
            }}
          >
            <div className="featured-project-media">
              {featured.image ? (
                <img
                  src={featured.image}
                  alt={featured.title}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="project-no-preview">No preview available</span>
              )}
            </div>

            <div className="featured-project-body">
              <p className="public-eyebrow">Featured project</p>
              <h3 className="featured-project-title">{featured.title}</h3>
              {featured.description && (
                <p className="project-card-description">
                  {featured.description}
                </p>
              )}
              <p className="project-meta">
                {[featured.role, featured.period, featured.status]
                  .filter(Boolean)
                  .join(" · ")}
              </p>

              {featured.tags?.length > 0 && (
                <div className="chip-row">
                  {featured.tags.slice(0, 4).map((tag) => (
                    <span
                      className="public-chip public-chip--primary"
                      key={tag}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div
                className="project-actions"
                onClick={(event) => event.stopPropagation()}
              >
                {featured.slug && (
                  <button
                    type="button"
                    className="project-open"
                    onClick={() => openProject(featured)}
                  >
                    View case study
                    <ArrowUpRight size={15} />
                  </button>
                )}
                <ProjectLinks project={featured} />
              </div>
            </div>
          </article>
        )}

        <div className="row projects-grid">
          {gridProjects.map((project) => (
            <div className="col s12 m6 l4" key={project.id || project.slug}>
              <ProjectCard
                project={project}
                onOpen={() => openProject(project)}
              />
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="project-empty">
            <h3>No projects here yet</h3>
            <p>
              Nothing matches “{filter}”. Try another filter to see the rest of
              the work.
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <nav
            style={{
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
            }}
            className="project-pagination"
            aria-label="Project pages"
          >
            <button
              type="button"
              className="page-btn"
              aria-label="Previous page"
              disabled={page === 1}
              onClick={() => page > 1 && goPage(page - 1)}
            >
              <ChevronLeft size={17} />
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (number) => (
                <button
                  key={number}
                  type="button"
                  className={`page-btn${number === page ? " is-active" : ""}`}
                  aria-label={`Page ${number}`}
                  aria-current={number === page ? "page" : undefined}
                  onClick={() => goPage(number)}
                >
                  {number}
                </button>
              ),
            )}
            <button
              type="button"
              className="page-btn"
              aria-label="Next page"
              disabled={page === totalPages}
              onClick={() => page < totalPages && goPage(page + 1)}
            >
              <ChevronRight size={17} />
            </button>
          </nav>
        )}
      </div>
    </section>
  );
};

const ProjectCard = ({ project, onOpen }) => (
  <article
    className="project-card"
    role="link"
    tabIndex={project.slug ? 0 : undefined}
    aria-label={project.title}
    onClick={() => project.slug && onOpen()}
    onKeyDown={(event) => {
      if (project.slug && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onOpen();
      }
    }}
  >
    <div className="project-card-media">
      {project.image ? (
        <img
          src={project.image}
          alt={project.title}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span className="project-no-preview">No preview</span>
      )}
      <div
        className="project-card-links"
        onClick={(event) => event.stopPropagation()}
      >
        <ProjectLinks project={project} />
      </div>
    </div>

    <div className="project-card-body">
      <h3 className="project-card-title">{project.title}</h3>
      {project.description && (
        <p className="project-card-description">{project.description}</p>
      )}

      {project.tags?.length > 0 && (
        <div className="project-card-tags">
          {project.tags.slice(0, 3).map((tag) => (
            <span className="public-chip" key={tag}>
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="project-card-footer">
        <span className="project-period">
          {[project.period, project.role].filter(Boolean).join(" · ")}
        </span>
        {project.slug && (
          <span className="project-open" aria-hidden="true">
            Open
            <ArrowUpRight size={14} />
          </span>
        )}
      </div>
    </div>
  </article>
);

const ProjectLinks = ({ project }) => {
  if (!project.github && !project.website) return null;

  return (
    <div className="project-links">
      {project.github && (
        <a
          className="icon-link"
          href={project.github}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={`${project.title} source code`}
          onClick={(event) => event.stopPropagation()}
        >
          <Github size={15} />
        </a>
      )}
      {project.website && (
        <a
          className="icon-link"
          href={project.website}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={`${project.title} live site`}
          onClick={(event) => event.stopPropagation()}
        >
          <ExternalLink size={15} />
        </a>
      )}
    </div>
  );
};

export default Projects;
